import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Building2, MessageSquare, Menu, X, ChevronDown, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ADANA_DISTRICTS } from "@/data/adana";

export function HeaderNavigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Ana Sayfa", path: "/" },
    {
      name: "İlanlar",
      path: "/satilik",
      subItems: [
        { name: "Tüm Satılık Portföyler", path: "/satilik" },
        { name: "Tüm Kiralık Portföyler", path: "/kiralik" },
        { name: "⭐ Lüks Projeler & Rezidanslar", path: "/luks-projeler" },
        { name: "🏢 Ticari Gayrimenkul & Dükkan", path: "/ticari-gayrimenkul" },
        { name: "🏕️ Satılık Arsa & Arazi", path: "/arsa" },
      ]
    },
    {
      name: "Bölgeler",
      path: "/adana",
      subItems: [
        { name: "Adana'nın tüm ilçeleri", path: "/adana" },
        ...ADANA_DISTRICTS.filter((district) => !['seyhan', 'cukurova'].includes(district.slug)).map((district) => ({ name: `${district.name} Emlak`, path: `/adana/${district.slug}` })),
        { name: "Seyhan Emlak", path: "/adana/seyhan" },
        { name: "Gürselpaşa Satılık Daire", path: "/adana/seyhan/gurselpasa" },
        { name: "Pınar Mahallesi", path: "/adana/seyhan/pinar" },
        { name: "Çukurova Emlak", path: "/adana/cukurova" },
        { name: "Güzelyalı Satılık Daire", path: "/adana/cukurova/guzelyali" },
        { name: "Mahfesığmaz Mahallesi", path: "/adana/cukurova/mahfesigmaz" },
      ]
    },
    {
      name: "Blog & Rehber",
      path: "/blog",
      subItems: [
        { name: "📚 Tüm Emlak Rehberleri & Blog (20)", path: "/blog" },
        { name: "📌 Adana Gayrimenkul Rehberi", path: "/adana-gayrimenkul-rehberi" },
        { name: "💡 Corner Cevaplıyor (SSS)", path: "/corner-cevapliyor" },
        { name: "⚖️ Bölge Karşılaştırmaları", path: "/karsilastirma" },
        { name: "📊 Piyasa Değerlendirme Rehberi", path: "/raporlar/adana-gayrimenkul-raporu-agustos-2026" },
      ]
    },
    {
      name: "Hizmetler",
      path: "/hizmetler",
      subItems: [
        { name: "Tüm Hizmetlerimiz", path: "/hizmetler" },
        { name: "Gayrimenkul Danışmanlığı", path: "/hizmetler/gayrimenkul-danismanligi" },
        { name: "Yatırım Danışmanlığı", path: "/hizmetler/yatirim-danismanligi" },
        { name: "Mülk Değerleme & Ekspertiz", path: "/degerleme" },
        { name: "Mülkünü Sat", path: "/gayrimenkulumu-sat" },
        { name: "🛠️ Tadilat & Dekorasyon", path: "/tadilat-dekorasyon" },
      ]
    },
    {
      name: "Kurumsal",
      path: "/hakkimizda",
      subItems: [
        { name: "Hakkımızda", path: "/hakkimizda" },
        { name: "Ekibimiz & Danışmanlar", path: "/ekibimiz" },
        { name: "Kira Sözleşmesi Örneği", path: "/kira-sozlesmesi" },
        { name: "İletişim", path: "/iletisim" },
      ]
    },
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-md border-b border-border/80 shadow-xs">
      {/* Hidden Internal Spiritual Note */}
      <span className="sr-only" data-blessing="Hasbunallah wa ni'mal wakeel">Hasbünallahü ve ni'me'l-vekîl</span>

      {/* Top Contact Bar */}
      <div className="bg-primary text-primary-foreground py-1.5 px-4 text-xs font-medium">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline flex items-center gap-1">
              <MapPin className="h-3 w-3" /> Gürselpaşa Mah. Aliya İzzetbegoviç Bulv. Seyhan / Adana
            </span>
            <span className="sm:hidden flex items-center gap-1">
              <MapPin className="h-3 w-3" /> Adana Seyhan & Çukurova Emlak
            </span>
          </div>

          <div className="flex items-center gap-4">
            <a href="https://wa.me/905335487801" target="_blank" rel="noreferrer" className="hover:underline flex items-center gap-1 text-green-300 font-bold">
              <MessageSquare className="h-3 w-3" /> WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Brand Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-primary-foreground shadow-md group-hover:scale-105 transition-transform">
            <Building2 className="h-6 w-6" />
          </div>
          <div>
            <span className="font-extrabold text-xl tracking-tight text-foreground group-hover:text-primary transition-colors block leading-none">
              CORNER
            </span>
            <span className="text-[10px] font-bold tracking-widest text-muted-foreground uppercase block mt-0.5">
              GAYRİMENKUL ADANA
            </span>
          </div>
        </Link>

        {/* Desktop Navigation Links */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path || (link.subItems && link.subItems.some((s) => s.path === location.pathname));
            if (link.subItems) {
              return (
                <div key={link.name} className="relative group py-2">
                  <Link
                    to={link.path}
                    className={`px-3 py-2 rounded-lg text-sm font-bold flex items-center gap-1 transition-all ${
                      isActive ? 'text-primary bg-primary/10' : 'text-foreground/80 hover:text-primary hover:bg-muted/70'
                    }`}
                  >
                    {link.name}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                  </Link>

                  {/* Dropdown Menu */}
                  <div className="absolute top-full left-0 w-64 max-h-[70vh] overflow-y-auto bg-card border border-border rounded-2xl shadow-xl p-2 hidden group-hover:block group-focus-within:block animate-in fade-in-50 slide-in-from-top-2 duration-200 z-50">
                    {link.subItems.map((sub) => (
                      <Link
                        key={sub.name}
                        to={sub.path}
                        className={`block px-3 py-2.5 text-xs font-bold rounded-xl transition-colors ${
                          location.pathname === sub.path
                            ? 'text-primary bg-primary/10'
                            : 'text-foreground/80 hover:text-primary hover:bg-muted/80'
                        }`}
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={link.name}
                to={link.path}
                className={`px-3 py-2 rounded-lg text-sm font-bold transition-all ${
                  isActive ? 'text-primary bg-primary/10' : 'text-foreground/80 hover:text-primary hover:bg-muted/70'
                }`}
              >
                {link.name}
              </Link>
            );
          })}
        </nav>

        {/* Action Buttons & Lang Selector */}
        <div className="flex items-center gap-2">
          <Button asChild size="sm" className="hidden md:inline-flex font-bold shadow-md rounded-xl">
            <a href="https://wa.me/905335487801?text=Merhaba%20Corner%20Gayrimenkul,%20danışmanlık%20almak%20istiyorum." target="_blank" rel="noreferrer">
              <MessageSquare className="h-4 w-4 mr-1.5 text-green-400" />
              Danışman İle Görüş
            </a>
          </Button>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-xl text-foreground hover:bg-muted"
            aria-label="Menü"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-card border-b border-border px-4 pt-2 pb-6 space-y-2 animate-in slide-in-from-top-4 duration-200">
          {navLinks.map((link) => (
            <div key={link.name}>
              <Link
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2.5 rounded-xl text-base font-black text-foreground hover:bg-muted"
              >
                {link.name}
              </Link>
              {link.subItems && (
                <div className="pl-4 space-y-1">
                  {link.subItems.map((sub) => (
                    <Link
                      key={sub.name}
                      to={sub.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-3 py-2 rounded-lg text-xs font-bold text-muted-foreground hover:text-primary hover:bg-muted/60"
                    >
                      • {sub.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="pt-4 border-t border-border flex flex-col gap-2">
            <Button asChild size="lg" className="w-full font-bold rounded-xl">
              <a href="https://wa.me/905335487801" target="_blank" rel="noreferrer">
                <MessageSquare className="h-4 w-4 mr-2" />
                WhatsApp Danışma Hattı
              </a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
